import { useMemo } from 'react';
import classnames from 'classnames';
import { MdOutlineInfo } from 'react-icons/md';
import { parseZonedDateTime } from '@internationalized/date';
import { Tooltip } from '@ds';
import { CommonRouteLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { convertMsToTimeString, getTimeDifference } from '@/utils/helpers';
import { RouteLegTypeEnum } from '@/api/apis/route';
import { useWatchFields } from '@/hooks/useWatchFields';

const CALCULATED_DURATION_TOOLTIP_TEXT_AVIA =
  'Automatically calculated flight time based on departure locations, block time, and other factors.';
const CALCULATED_DURATION_TOOLTIP_TEXT_GROUND =
  'Automatically calculated based on departure time, locations, drive duration, traffic and other factors.';
const ACTUAL_DURATION_TOOLTIP_TEXT =
  'Manually set departure and arrival times. May differ from Calculated duration when departure and arrival times are unlocked';

const LEG_TYPES_WITHOUT_DURATION = [
  RouteLegTypeEnum.CurriServiceGroundLeg,
  RouteLegTypeEnum.CommercialFlightAviaLeg,
  RouteLegTypeEnum.AccommodationLeg,
];

type DurationBlockProps = {
  title: string;
  duration: string;
  tooltipText: string;
  withBackground?: boolean;
};

const DurationBlock = ({ duration, title, tooltipText, withBackground = false }: DurationBlockProps) => {
  const breakTitle = title.split(' ');

  return (
    <div className={classnames('p-2 flex gap-2 items-center rounded-lg', { 'bg-base-70': withBackground })}>
      <span className="font-secondary text-hm text-base-5">{duration}</span>
      <div className="flex flex-col">
        {breakTitle.map((word) => (
          <span key={word} className="text-s-r text-base-5">
            {word}
          </span>
        ))}
      </div>
      <Tooltip content={tooltipText} closeDelay={0}>
        <div className="text-black">
          <MdOutlineInfo size={16} />
        </div>
      </Tooltip>
    </div>
  );
};

export const LegDuration = () => {
  const { legType, timeDurationInMs, departureDateTime, arrivalDateTime } = useWatchFields<CommonRouteLegSchemaValues>([
    'legType',
    'timeDurationInMs',
    'departureDateTime',
    'arrivalDateTime',
  ] as const);

  const calculatedDuration = useMemo(() => {
    if (timeDurationInMs) {
      return convertMsToTimeString(timeDurationInMs);
    }

    return '-h';
  }, [timeDurationInMs]);

  const actualDuration = useMemo(() => {
    if (departureDateTime && arrivalDateTime) {
      const departureUtc = parseZonedDateTime(departureDateTime).toAbsoluteString();
      const arrivalUtc = parseZonedDateTime(arrivalDateTime).toAbsoluteString();
      const diff = getTimeDifference(new Date(departureUtc), new Date(arrivalUtc));
      return convertMsToTimeString(diff.MILLISECONDS);
    }

    return '-h';
  }, [departureDateTime, arrivalDateTime]);

  const showDuration = !LEG_TYPES_WITHOUT_DURATION.includes(legType);

  if (!showDuration) return null;

  const isGroundLeg =
    legType === RouteLegTypeEnum.TmdxAffiliateGroundLeg ||
    legType === RouteLegTypeEnum.TxCenterProviderGroundLeg ||
    legType === RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg ||
    legType === RouteLegTypeEnum.TmdxDriveTedsGroundLeg ||
    legType === RouteLegTypeEnum.SelfServicedGroundLeg;
  const calculatedTooltipText = isGroundLeg
    ? CALCULATED_DURATION_TOOLTIP_TEXT_GROUND
    : CALCULATED_DURATION_TOOLTIP_TEXT_AVIA;

  return (
    <div className="flex gap-4">
      <DurationBlock title="Calculated duration" duration={calculatedDuration} tooltipText={calculatedTooltipText} />
      <DurationBlock
        title="Actual duration"
        duration={actualDuration}
        tooltipText={ACTUAL_DURATION_TOOLTIP_TEXT}
        withBackground
      />
    </div>
  );
};
